import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, ClipboardList, Loader2, MapPin, Clock, Users,
  CheckCircle2, UserX, RefreshCw, ChevronRight, Flag, Lock, BarChart3
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusOperatorManifest = () => {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [loadingManifest, setLoadingManifest] = useState(false);
  const [actioning, setActioning] = useState(null);
  const [departing, setDeparting] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('tekstar_token')) {
      navigate('/bus/operator/login', { state: { returnTo: '/bus/operator/manifest' } });
      return;
    }
    fetchTrips();
  }, [navigate]);

  const fetchTrips = async () => {
    setLoadingTrips(true);
    try {
      const res = await api.get('/bus/operators/trips/today?include_departed=true');
      setTrips(res.data.trips || []);
    } catch (e) {
      toast({ title: 'Could not load trips', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setLoadingTrips(false);
    }
  };

  const loadManifest = async (trip) => {
    setSelectedTrip(trip);
    setLoadingManifest(true);
    setManifest(null);
    try {
      const res = await api.get(`/bus/operators/trips/${trip.id}/manifest`);
      setManifest(res.data);
    } catch (e) {
      toast({ title: 'Could not load manifest', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setLoadingManifest(false);
    }
  };

  const refresh = () => { if (selectedTrip) loadManifest(selectedTrip); };

  const boardPassenger = async (row) => {
    setActioning(row.bookingId + '-board');
    try {
      await api.post('/bus/tickets/board', { ticket_code: row.ticketCode });
      toast({ title: 'Boarded', description: `${row.name} (${row.seat}) checked in.` });
      refresh();
    } catch (e) {
      toast({ title: 'Could not board', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setActioning(null);
    }
  };

  const markNoShow = async (row) => {
    setActioning(row.bookingId + '-noshow');
    try {
      await api.put(`/bus/operators/bookings/${row.bookingId}/mark-no-show`);
      toast({ title: 'Marked no-show', description: `Seat ${row.seat} released.` });
      refresh();
    } catch (e) {
      toast({ title: 'Could not mark', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setActioning(null);
    }
  };

  const departTrip = async () => {
    if (!selectedTrip) return;
    const awaiting = manifest?.counts?.notBoarded || 0;
    if (!window.confirm(`Close manifest and depart? ${awaiting} un-boarded ticket(s) will be flagged no-show and boarding will be locked.`)) return;
    setDeparting(true);
    try {
      const res = await api.post(`/bus/operators/trips/${selectedTrip.id}/depart`);
      toast({ title: 'Trip departed', description: res.data.message });
      refresh();
    } catch (e) {
      toast({ title: 'Could not depart', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setDeparting(false);
    }
  };

  const StatCard = ({ label, value, color }) => (
    <div className={`rounded-lg p-3 text-center ${color}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs">{label}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/bus/operator')} data-testid="manifest-back-btn" className="text-gray-500 hover:text-gray-800">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <ClipboardList className="w-6 h-6 text-indigo-600" />
          <div>
            <h1 className="text-lg font-bold leading-tight">Boarding Manifest</h1>
            <p className="text-xs text-gray-500">Live boarded vs booked, with no-show flagging</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {!selectedTrip ? (
          <Card className="p-5" data-testid="manifest-trip-list">
            <h2 className="text-base font-semibold mb-3">Select a trip</h2>
            {loadingTrips ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
            ) : trips.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">No upcoming trips in the next 3 days.</p>
            ) : (
              <div className="space-y-2">
                {trips.map((t) => (
                  <button key={t.id} onClick={() => loadManifest(t)} data-testid={`manifest-trip-${t.id}`}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-gray-400" />
                        {t.route?.origin?.city} → {t.route?.destination?.city}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" /> {t.date} · {t.departureTime}
                        {t.status === 'departed' && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">DEPARTED</span>
                        )}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                ))}
              </div>
            )}
          </Card>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={() => { setSelectedTrip(null); setManifest(null); }} data-testid="manifest-change-trip-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Change trip
              </Button>
              <Button variant="outline" size="sm" onClick={refresh} disabled={loadingManifest} data-testid="manifest-refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingManifest ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {manifest && manifest.trip?.status !== 'departed' && (
              <Button onClick={departTrip} disabled={departing}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white" data-testid="depart-trip-btn">
                {departing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Flag className="w-4 h-4 mr-2" />}
                Close manifest & depart
              </Button>
            )}

            {loadingManifest ? (
              <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-indigo-600" /></div>
            ) : manifest && (
              <>
                {manifest.trip?.status === 'departed' && (
                  <div className="rounded-lg border-2 border-gray-300 bg-gray-100 text-gray-700 p-4 flex items-center gap-2" data-testid="departed-banner">
                    <Lock className="w-5 h-5" />
                    <span className="font-medium">Trip departed — boarding closed.</span>
                  </div>
                )}
                <Card className="p-5">
                  <p className="text-sm font-medium flex items-center gap-1 mb-1">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    {manifest.route?.origin?.city} → {manifest.route?.destination?.city}
                  </p>
                  <p className="text-xs text-gray-500 mb-4">{manifest.trip?.date} · {manifest.trip?.departureTime}</p>
                  <div className="grid grid-cols-4 gap-2" data-testid="manifest-counts">
                    <StatCard label="Booked" value={manifest.counts.booked} color="bg-gray-100 text-gray-700" />
                    <StatCard label="Boarded" value={manifest.counts.boarded} color="bg-green-100 text-green-700" />
                    <StatCard label="Awaiting" value={manifest.counts.notBoarded} color="bg-amber-100 text-amber-700" />
                    <StatCard label="No-show" value={manifest.counts.noShow} color="bg-red-100 text-red-700" />
                  </div>
                </Card>

                {manifest.summary && (
                  <Card className={`p-5 ${manifest.trip?.status === 'departed' ? 'border-2 border-indigo-200 bg-indigo-50/40' : ''}`} data-testid="trip-summary">
                    <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-indigo-600" /> Trip summary
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Load factor</p>
                        <p className="text-xl font-bold">{manifest.summary.loadFactorPct}%</p>
                        <p className="text-[11px] text-gray-400">{manifest.counts.boarded}/{manifest.counts.totalSeats} seats</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">No-show rate</p>
                        <p className="text-xl font-bold">{manifest.summary.noShowRatePct}%</p>
                        <p className="text-[11px] text-gray-400">{manifest.counts.noShow} no-show</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Collected</p>
                        <p className="text-xl font-bold text-green-700">K{manifest.summary.revenueCollected}</p>
                        <p className="text-[11px] text-gray-400">of K{manifest.summary.revenueTotal}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Pending</p>
                        <p className="text-xl font-bold text-amber-700">K{manifest.summary.revenuePending}</p>
                        <p className="text-[11px] text-gray-400">to collect</p>
                      </div>
                    </div>
                  </Card>
                )}

                <Card className="p-0 overflow-hidden">
                  <div className="px-5 py-3 border-b bg-gray-50 flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium">Passengers ({manifest.passengers.length})</span>
                  </div>
                  {manifest.passengers.length === 0 ? (
                    <p className="text-sm text-gray-500 py-8 text-center">No bookings yet for this trip.</p>
                  ) : (
                    <div className="divide-y">
                      {manifest.passengers.map((p, i) => (
                        <div key={i} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`manifest-row-${p.seat}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50 text-indigo-700 font-bold text-sm shrink-0">{p.seat}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{p.name}</p>
                              <p className="text-xs text-gray-500 truncate">{p.ticketCode} · {p.paymentStatus}{p.isGuest ? ' · guest' : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {p.cancelled ? (
                              <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500">Cancelled</span>
                            ) : p.noShow ? (
                              <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">No-show</span>
                            ) : p.boarded ? (
                              <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Boarded
                              </span>
                            ) : manifest.trip?.status === 'departed' ? (
                              <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500">Closed</span>
                            ) : (
                              <>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8"
                                  disabled={actioning === p.bookingId + '-board'}
                                  onClick={() => boardPassenger(p)} data-testid={`board-btn-${p.seat}`}>
                                  {actioning === p.bookingId + '-board' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Board'}
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                                  disabled={actioning === p.bookingId + '-noshow'}
                                  onClick={() => markNoShow(p)} data-testid={`noshow-btn-${p.seat}`}>
                                  <UserX className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BusOperatorManifest;
